/**
 * useNativeBridge — the web side of the Buzztime ProBook ⇄ React Native WebView
 * bridge. Single surface for talking to the native shell and being talked to.
 *
 * Protocol version: 3  (keep in sync with WEBVIEW_BRIDGE_PROTOCOL.md)
 *   v2 adds the location request/response pair (LOCATION_GET → LOCATION_RESULT,
 *   LOCATION_OPEN_SETTINGS) and the injected `capabilities` list. ADDITIVE: the
 *   wire envelope stays { v: 1, ... } — deployed v1 receive()s drop v !== 1, so
 *   the envelope version and this protocol/feature version are distinct things.
 *   Feature-detect with ctx.capabilities (v1 shells inject none) — never by
 *   protocol number.
 *   v3 tightens the auth handoff (BB-951) — no new messages, no detection needed:
 *   /api/auth/bridge always re-verifies X-Bets-Session (a live probook_session no
 *   longer short-circuits), the web POSTs /auth/logout on REQUEST_LOGOUT and
 *   SESSION_INVALIDATED, and the native shell clears the probook_session cookie
 *   from the WebView jar on bets sign-out.
 *
 * Transport
 *   web → native : window.ReactNativeWebView.postMessage(JSON.stringify(envelope))
 *   native → web : webViewRef.injectJavaScript("window.__btBridge.receive(<json>); true;")
 *
 * Detection (SSR-safe, no flash of web-only chrome)
 *   1. ?platform=ios-app|android-app on the source URI  → readable during Nuxt SSR.
 *   2. window.ReactNativeWebView present                → client confirmation.
 *   3. window.__BUZZTIME_NATIVE__ (injectedJavaScriptBeforeContentLoaded)
 *      → platform, appVersion, env, safe-area insets.
 *
 * The inbound handler registry, back-interceptor stack, and the single
 * window.__btBridge installation are MODULE-SCOPED so every useNativeBridge()
 * caller (layout, useAuth, pages, overlays) shares one channel.
 */

export type NativePlatform = 'ios' | 'android'

export interface NativeContext {
  platform: NativePlatform | null
  appVersion: string | null
  env: 'prod' | 'dev' | null
  insets: { top: number; right: number; bottom: number; left: number }
  // CSS px occluded at the bottom by the native floating tab bar (0 if none).
  // This is the FULL distance from the WebView bottom to the bar's top edge:
  // tab-bar height + the OS safe area the bar floats above (= CONTENT_INSET.bottom
  // / BOOT obstructedBottom). It therefore ALREADY subsumes insets.bottom — the
  // web reserves exactly this much (via max(), never summed with the safe area)
  // so content clears the bar without double-counting.
  obstructedBottom: number
  // native feature flags injected via __BUZZTIME_NATIVE__ (v2 shells send
  // ['location', ...]; absent on v1 shells -> web falls back to the browser API)
  capabilities?: string[]
}

// LOCATION_RESULT payload (native replies to LOCATION_GET with the same id).
// mocked = android's mock-provider flag, null on ios/unknown — the server's
// hard anti-spoof signal, forwarded verbatim, never stripped.
export interface LocationSample { lat: number; lon: number; accuracy_m: number | null; ts_ms: number | null }
export interface LocationResultPayload {
  status: 'ok' | 'denied' | 'denied_forever' | 'unavailable' | 'timeout' | 'error'
  samples?: LocationSample[]
  mocked?: boolean | null
}

export type OutboundType =
  | 'WEBVIEW_READY' | 'AUTH_OK' | 'AUTH_REQUIRED' | 'BALANCE_CHANGED'
  | 'NAV_STATE' | 'BACK_HANDLED' | 'OPEN_EXTERNAL' | 'REQUEST_LOGOUT'
  | 'HAPTIC' | 'LOG' | 'LOCATION_GET' | 'LOCATION_OPEN_SETTINGS'

export type InboundType =
  | 'NAV_BACK' | 'NAV_TO' | 'REFRESH' | 'SET_BALANCE'
  | 'SAFE_AREA' | 'CONTENT_INSET' | 'LIFECYCLE' | 'SESSION_INVALIDATED'
  | 'LOCATION_RESULT'

interface Envelope<T extends string = string> { v: 1; type: T; id?: string; payload?: any }

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage(data: string): void }
    __BUZZTIME_NATIVE__?: Partial<NativeContext>
    __btBridge?: { receive(json: string | object): void }
  }
}

// A back interceptor returns true if it consumed the OS back event (e.g. closed
// an overlay) and false to let it bubble (router back -> native pop/exit). Newest
// runs first.
type BackInterceptor = () => boolean

// ---- module-scoped, shared across all callers --------------------------------
const _handlers = new Map<InboundType, Set<(payload: any, id?: string) => void>>()
const _backStack: BackInterceptor[] = []
let _installed = false

function _post(type: OutboundType, payload?: any, id?: string) {
  if (typeof window === 'undefined') return
  const rn = window.ReactNativeWebView
  if (!rn) return
  const env: Envelope<OutboundType> = { v: 1, type, ...(id ? { id } : {}), ...(payload !== undefined ? { payload } : {}) }
  try { rn.postMessage(JSON.stringify(env)) } catch { /* never throw across the bridge */ }
}

export function useNativeBridge() {
  const route = useRoute()

  // ---- detection -------------------------------------------------------------
  const queryFlag = String(route.query.platform || '')
  const ssrEmbedded = /(?:ios|android)-app/.test(queryFlag)
  const isNative = useState<boolean>('bt_isNative', () => ssrEmbedded)
  const ctx = useState<NativeContext>('bt_nativeCtx', () => ({
    platform: queryFlag.startsWith('ios') ? 'ios' : queryFlag.startsWith('android') ? 'android' : null,
    appVersion: null,
    env: queryFlag ? (route.query.env === 'dev' ? 'dev' : 'prod') : null,
    insets: { top: 0, right: 0, bottom: 0, left: 0 },
    obstructedBottom: 0,
  }))

  function on(type: InboundType, fn: (payload: any, id?: string) => void) {
    if (!_handlers.has(type)) _handlers.set(type, new Set())
    _handlers.get(type)!.add(fn)
    return () => _handlers.get(type)?.delete(fn)
  }

  function registerBack(fn: BackInterceptor) {
    _backStack.push(fn)
    return () => { const i = _backStack.lastIndexOf(fn); if (i >= 0) _backStack.splice(i, 1) }
  }

  // one-shot request/response over the bridge, paired by envelope id (the
  // NAV_BACK -> BACK_HANDLED echo pattern, but web-initiated). Rejects on
  // timeout — callers treat that as 'unavailable', never hang.
  function request<T = any>(out: OutboundType, expect: InboundType, payload?: any, timeoutMs = 12000): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const id = newUlid()
      let done = false
      const off = on(expect, (p: any, rid?: string) => {
        if (done || rid !== id) return
        done = true; off(); clearTimeout(timer)
        resolve(p as T)
      })
      const timer = setTimeout(() => {
        if (done) return
        done = true; off()
        reject(new Error('bridge-timeout'))
      }, timeoutMs)
      _post(out, payload, id)
    })
  }

  function handleNativeBack(id?: string) {
    for (let i = _backStack.length - 1; i >= 0; i--) {
      try { if (_backStack[i]()) { _post('BACK_HANDLED', { handled: true }, id); return } } catch { /* ignore */ }
    }
    const canGoBack = typeof window !== 'undefined' && window.history.length > 1 && route.path !== '/'
    if (canGoBack) { useRouter().back(); _post('BACK_HANDLED', { handled: true }, id) }
    else { _post('BACK_HANDLED', { handled: false }, id) }
  }

  const api = {
    isNative,
    ctx,
    post: _post,
    on,
    registerBack,
    ready(extra?: Record<string, any>) { _post('WEBVIEW_READY', { path: route.path, protocol: 3, ...extra }) },
    balanceChanged(available_balance: number) { _post('BALANCE_CHANGED', { available_balance }) },
    navState(opts: { canGoBack: boolean; overlayOpen: boolean }) { _post('NAV_STATE', { path: route.path, ...opts }) },
    authRequired(reason: string) { _post('AUTH_REQUIRED', { reason }) },
    authOk(p: { id: number; username: string }) { _post('AUTH_OK', p) },
    openExternal(url: string) { _post('OPEN_EXTERNAL', { url }) },
    requestLogout() { _post('REQUEST_LOGOUT') },
    haptic(kind: 'light' | 'success' | 'warning') { _post('HAPTIC', { kind }) },
    // device location from the native shell (expo-location). Gate calls on
    // ctx.capabilities?.includes('location') — v1 shells never reply and the
    // timeout would burn 12s for nothing.
    getLocation(opts?: { samples?: number; timeoutMs?: number }): Promise<LocationResultPayload> {
      const timeoutMs = opts?.timeoutMs ?? 12000
      return request<LocationResultPayload>('LOCATION_GET', 'LOCATION_RESULT', {
        high_accuracy: true,
        samples: opts?.samples ?? 3,
        timeout_ms: timeoutMs,
      }, timeoutMs + 3000)  // native owns timeout_ms; the web waits a little longer
    },
    // deep-link to the OS app-settings page (recovery from denied_forever)
    openLocationSettings() { _post('LOCATION_OPEN_SETTINGS') },
  }

  // ---- client wiring (install once) -----------------------------------------
  if (typeof window !== 'undefined') {
    // NOTE: isNative is intentionally NOT set from window here. It must equal the
    // SSR value (derived from ?platform) at first render, or hydration diverges
    // (footer/sign-out + the conditional async layout differ). window-based
    // detection can't help SSR anyway — the embed signal must be on the URL.
    const injected = window.__BUZZTIME_NATIVE__
    if (injected) {
      ctx.value = { ...ctx.value, ...injected, insets: { ...ctx.value.insets, ...(injected.insets || {}) } }
    }

    if (!_installed) {
      _installed = true
      window.__btBridge = {
        receive(raw: string | object) {
          let env: Envelope<InboundType>
          try { env = typeof raw === 'string' ? JSON.parse(raw) : (raw as any) } catch { return }
          if (!env || env.v !== 1 || !env.type) return
          if (env.type === 'NAV_BACK') { handleNativeBack(env.id); return }
          if (env.type === 'SAFE_AREA' && env.payload) {
            ctx.value = { ...ctx.value, insets: { ...ctx.value.insets, ...env.payload } }
          }
          // floating tab-bar height changed (shown/hidden, rotation, etc.)
          if (env.type === 'CONTENT_INSET' && env.payload && typeof env.payload.bottom === 'number') {
            ctx.value = { ...ctx.value, obstructedBottom: env.payload.bottom }
          }
          const set = _handlers.get(env.type)
          if (set) set.forEach((fn) => { try { fn(env.payload, env.id) } catch { /* ignore */ } })
        },
      }
    }
  }

  return api
}
